import { useState, type ReactNode } from "react";
import { UserCheck } from "lucide-react";

import type { Class, ClassTeacherCandidate } from "../../services/studentService";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { classConfig, levelConfig, levelRows, occupancy, teacherConfig, teacherRows, type LevelRow, type TeacherRow } from "./classSetupLogic";

const Fill = ({ learners, capacity }: { learners: number; capacity: number }) => {
  const fill = occupancy(learners, capacity);
  return (
    <span className="flex items-center gap-2" title={fill.label}>
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-100">
        <span className={`block h-full rounded-full ${fill.bar}`} style={{ width: `${Math.min(100, fill.percent)}%` }} />
      </span>
      <span className="tabular-nums text-gray-700">{fill.percent}%</span>
    </span>
  );
};

const places = (free: number) => (free < 0 ? <span className="font-semibold text-red-700">{free}</span> : free);

/** Every class of the year, with how full it is and who leads it. */
export function ClassesPanel({ rows, yearName, canWrite, isLoading, isError, onRetry, onOpen, actions, initialState }: {
  rows?: Class[];
  yearName: string;
  canWrite: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen: (row: Class) => void;
  actions?: ReactNode;
  initialState?: { view?: string; category?: string };
}) {
  const columns: RegisterColumn<Class>[] = [
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Level", cell: (row) => row.level, exportValue: (row) => row.level },
    { header: "Stream", cell: (row) => row.section || <span className="text-gray-400">—</span>, exportValue: (row) => row.section || "" },
    { header: "Class teacher", cell: (row) => row.class_teacher_name || <span className="text-amber-700">Not assigned</span>, exportValue: (row) => row.class_teacher_name || "Not assigned" },
    { header: "Room", cell: (row) => row.room || <span className="text-gray-400">—</span>, exportValue: (row) => row.room || "" },
    { header: "Learners", cell: (row) => row.current_student_count, exportValue: (row) => row.current_student_count, align: "right" },
    { header: "Boys", cell: (row) => row.boys ?? 0, exportValue: (row) => row.boys ?? 0, exportOnly: true },
    { header: "Girls", cell: (row) => row.girls ?? 0, exportValue: (row) => row.girls ?? 0, exportOnly: true },
    { header: "Places", cell: (row) => row.capacity, exportValue: (row) => row.capacity, align: "right" },
    { header: "Free", cell: (row) => places(row.available_spots), exportValue: (row) => row.available_spots, align: "right" },
    { header: "Full", cell: (row) => <Fill learners={row.current_student_count} capacity={row.capacity} />, exportValue: (row) => `${occupancy(row.current_student_count, row.capacity).percent}%` },
    { header: "Status", cell: (row) => { const fill = occupancy(row.current_student_count, row.capacity); return <StatusDot label={fill.label} dot={fill.dot} />; }, exportValue: (row) => occupancy(row.current_student_count, row.capacity).label, exportOnly: true },
  ];
  return (
    <ClientRegister
      key={JSON.stringify(initialState ?? {})}
      title={`Classes in ${yearName}`}
      description="Each stream is a class of its own, with its places, room and class teacher. Learners counted are those in school now. Open a class for its class list and subject teachers."
      actions={actions}
      rows={rows}
      config={classConfig}
      initialState={initialState}
      columns={columns}
      rowKey={(row) => row.id}
      onOpen={onOpen}
      openLabel={() => (canWrite ? "Manage" : "Open")}
      noun={["class", "classes"]}
      exportName={{ file: `classes-${yearName.replace(/\s+/g, "-").toLowerCase()}`, title: `Classes and Streams — ${yearName}` }}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      emptyText={`${yearName} has no classes yet. Add a level and its streams, or copy last year's classes.`}
    />
  );
}

/** The year's levels, each with its streams added up. */
export function LevelsPanel({ rows, yearName, isLoading, isError, onRetry, onOpen }: {
  rows?: Class[];
  yearName: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen: (level: string) => void;
}) {
  const columns: RegisterColumn<LevelRow>[] = [
    { header: "Level", cell: (row) => <span className="font-medium text-gray-900">{row.level}</span>, exportValue: (row) => row.level },
    { header: "Streams", cell: (row) => <span><span className="tabular-nums">{row.classes.length}</span> <span className="text-gray-500">· {row.streams}</span></span>, exportValue: (row) => row.streams },
    { header: "Learners", cell: (row) => row.learners, exportValue: (row) => row.learners, align: "right" },
    { header: "Boys", cell: (row) => row.boys, exportValue: (row) => row.boys, align: "right" },
    { header: "Girls", cell: (row) => row.girls, exportValue: (row) => row.girls, align: "right" },
    { header: "Places", cell: (row) => row.capacity, exportValue: (row) => row.capacity, align: "right" },
    { header: "Free", cell: (row) => row.free, exportValue: (row) => row.free, align: "right" },
    { header: "Full", cell: (row) => <Fill learners={row.learners} capacity={row.capacity} />, exportValue: (row) => `${occupancy(row.learners, row.capacity).percent}%` },
    { header: "Class teachers", cell: (row) => (row.withoutTeacher ? <span className="text-amber-700">{row.withoutTeacher} missing</span> : <span className="text-emerald-700">All assigned</span>), exportValue: (row) => (row.withoutTeacher ? `${row.withoutTeacher} missing` : "All assigned") },
  ];
  return (
    <ClientRegister
      title={`Levels in ${yearName}`}
      description="Each level with its streams added up: how many learners it holds and how many places are left. Open a level to see its classes."
      rows={rows ? levelRows(rows) : undefined}
      config={levelConfig}
      columns={columns}
      rowKey={(row) => row.level}
      onOpen={(row) => onOpen(row.level)}
      openLabel={() => "Classes"}
      noun={["level", "levels"]}
      exportName={{ file: `levels-${yearName.replace(/\s+/g, "-").toLowerCase()}`, title: `Levels — ${yearName}` }}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      emptyText={`${yearName} has no classes yet.`}
    />
  );
}

/** Who leads which class, and who is free to take one. */
export function TeachersPanel({ teachers, classes, yearName, canWrite, busy, isLoading, isError, onRetry, onOpenClass, onAssign }: {
  teachers?: ClassTeacherCandidate[];
  classes: Class[];
  yearName: string;
  canWrite: boolean;
  busy: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpenClass: (row: Class) => void;
  onAssign: (row: Class, teacher: TeacherRow) => void;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const rows = teachers ? teacherRows(teachers, classes) : undefined;
  const teacher = rows?.find((row) => row.id === viewing);
  const open = classes.filter((row) => !row.class_teacher);
  const columns: RegisterColumn<TeacherRow>[] = [
    { header: "Teacher", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Role", cell: (row) => row.role, exportValue: (row) => row.role },
    { header: "Leads", cell: (row) => (row.classes.length ? row.classes.map((item) => item.name).join(", ") : <span className="text-gray-400">No class</span>), exportValue: (row) => row.classes.map((item) => item.name).join(", ") },
    { header: "Classes", cell: (row) => (row.classes.length > 1 ? <span className="font-semibold text-amber-700">{row.classes.length}</span> : row.classes.length), exportValue: (row) => row.classes.length, align: "right" },
    { header: "Learners", cell: (row) => row.classes.reduce((sum, item) => sum + item.current_student_count, 0), exportValue: (row) => row.classes.reduce((sum, item) => sum + item.current_student_count, 0), align: "right" },
  ];
  return (
    <>
      <ClientRegister
        title={`Class teachers in ${yearName}`}
        description={`Every teacher who may lead a class, with the classes they lead this year. ${open.length ? `${open.length} ${open.length === 1 ? "class has" : "classes have"} no class teacher yet.` : "Every class has a class teacher."}`}
        rows={rows}
        config={teacherConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        openLabel={(row) => (canWrite && !row.classes.length && open.length ? "Assign" : "Open")}
        noun={["teacher", "teachers"]}
        exportName={{ file: `class-teachers-${yearName.replace(/\s+/g, "-").toLowerCase()}`, title: `Class Teachers — ${yearName}` }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No active teaching staff were found."
      />
      {teacher && (
        <DetailsModal
          title={teacher.name}
          headline={{ label: "Classes led", value: String(teacher.classes.length), note: teacher.classes.length ? `${teacher.classes.reduce((sum, item) => sum + item.current_student_count, 0)} learners` : `Not leading a class in ${yearName}` }}
          facts={[["Role", teacher.role], ["Leads", teacher.classes.map((item) => item.name).join(", ") || "—"]]}
          onClose={() => setViewing(null)}
        >
          {teacher.classes.length > 0 && (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {teacher.classes.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                  <span className="font-medium text-gray-900">{item.name}</span>
                  <span className="text-gray-500">{item.current_student_count} of {item.capacity} learners</span>
                  <button type="button" className="text-xs font-medium text-primary-700 hover:underline" onClick={() => { setViewing(null); onOpenClass(item); }}>Open class</button>
                </li>
              ))}
            </ul>
          )}
          {canWrite && open.length > 0 && (
            <ActionPanel title="Make them class teacher of" description="Classes this year with no class teacher yet.">
              <ul className="flex flex-wrap gap-2">
                {open.map((item) => (
                  <li key={item.id}>
                    <button type="button" disabled={busy} onClick={() => onAssign(item, teacher)} className="btn btn-outline h-8 gap-1.5 text-sm">
                      <UserCheck className="h-3.5 w-3.5" /> {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </ActionPanel>
          )}
        </DetailsModal>
      )}
    </>
  );
}
